import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';

const UPLOAD_ERROR_PATTERNS = [
  { key: 'mfg_missing', label: 'Manufacturing date missing', test: /manufacturing\s*date\s+is\s+required/i },
  { key: 'mfg_future', label: 'Future manufacturing date', test: /manufacturing\s+date\s+cannot\s+be\s+in\s+the\s+future/i },
  { key: 'invoice_qty', label: 'Invalid invoice quantity', test: /invoicequantity\s+must/i },
  { key: 'received_qty', label: 'Invalid received quantity', test: /receivedquantity\s+must/i },
  { key: 'batch_qty_count', label: 'Batch quantity count mismatch', test: /batchquantity\s+count/i },
  {
    key: 'batch_qty_total',
    label: 'Batch quantity mismatch',
    test: /total\s+batchquantity|batchquantity.*must\s+equal\s+receivedquantity/i,
  },
  { key: 'batch_qty_missing', label: 'Batch quantity missing', test: /batchquantity\s+is\s+required/i },
  { key: 'batch_missing', label: 'Batch data missing', test: /at\s+least\s+one\s+batch\s+is\s+required/i },
  { key: 'missing_required', label: 'Missing required fields', test: /missing\s+required|required\s+columns\s+not\s+found/i },
  { key: 'invoice_date', label: 'Invalid invoice date', test: /invoice\s+date/i },
  {
    key: 'customer_sku',
    label: 'Customer SKU Code error',
    test: /customer\s+sku\s+code|customerskucode|at\s+least\s+one\s+customer\s+sku/i,
  },
  {
    key: 'product_sku',
    label: 'Product SKU error',
    test: /product\s+sku|productsku|sku.*not\s+found|at\s+least\s+one\s+product\s+sku/i,
  },
  { key: 'company', label: 'Company not found', test: /company\s+not\s+found|not\s+onboarded/i },
  { key: 'product', label: 'Product not found', test: /product\s+not\s+found/i },
];

function normalizeUploadErrorText(message) {
  return String(message ?? '')
    .trim()
    .replace(/^row\s+\d+:\s*/i, '')
    .replace(/\s+from\s+row\s+\d+(\s+to\s+row\s+\d+)?.*$/i, '')
    .replace(/\s+in\s+rows\s+[\d,\s]+(?:to\s+\d+)?.*$/i, '');
}

function classifyUploadErrorMessage(message) {
  const core = normalizeUploadErrorText(message);
  if (!core) return 'other';
  for (const pattern of UPLOAD_ERROR_PATTERNS) {
    if (pattern.test.test(core) || pattern.test.test(message)) return pattern.key;
  }
  return 'other';
}

function collectUploadFailureMessages(details) {
  if (details == null) return [];
  if (typeof details === 'string') {
    const trimmed = details.trim();
    return trimmed ? [trimmed] : [];
  }
  if (Array.isArray(details)) {
    return details
      .map((item) => {
        if (typeof item === 'string') return item.trim();
        return String(item?.error ?? item?.message ?? item?.msg ?? '').trim();
      })
      .filter(Boolean);
  }

  const messages = [];
  const add = (value) => {
    const text = String(value ?? '').trim();
    if (text) messages.push(text);
  };

  const formatted =
    details?.result?.data?.formattedErrors ?? details?.data?.formattedErrors;
  if (Array.isArray(formatted) && formatted.length > 0) {
    formatted.forEach(add);
    return messages;
  }

  const errorArrays = [
    details?.result?.data?.errors,
    details?.data?.errors,
    details?.errors,
    details?.result?.errors,
    details?.details,
  ];
  for (const arr of errorArrays) {
    if (!Array.isArray(arr)) continue;
    for (const item of arr) {
      if (typeof item === 'string') add(item);
      else add(item?.error ?? item?.message ?? item?.msg);
    }
    if (messages.length > 0) return messages;
  }

  add(details?.error);
  add(details?.message);
  add(details?.result?.error);
  add(details?.result?.message);

  return messages;
}

export function summarizeUploadFailureMessages(details) {
  const messages = collectUploadFailureMessages(details);
  if (messages.length === 0) return null;

  const labels = [];
  const seen = new Set();
  for (const message of messages) {
    const key = classifyUploadErrorMessage(message);
    if (seen.has(key)) continue;
    seen.add(key);
    const pattern = UPLOAD_ERROR_PATTERNS.find((item) => item.key === key);
    labels.push(pattern?.label || 'Multiple validation errors');
  }

  const uniqueLabels = [...new Set(labels.filter(Boolean))];
  if (uniqueLabels.length === 0 || (uniqueLabels.length === 1 && uniqueLabels[0] === 'Multiple validation errors')) {
    return 'Multiple validation errors';
  }
  if (uniqueLabels.length === 1) return uniqueLabels[0];
  if (uniqueLabels.length === 2) return `${uniqueLabels[0]} + 1 more`;
  return `${uniqueLabels[0]} + ${uniqueLabels.length - 1} more`;
}

function buildUploadPreviewTitle(baseTitle, uploadStatus, failureDetails) {
  if (!uploadStatus || uploadStatus === 'idle') return baseTitle;
  if (uploadStatus === 'uploading') return `${baseTitle} (Uploading file...)`;
  if (uploadStatus === 'processing') return `${baseTitle} (Processing data, please wait...)`;
  if (uploadStatus === 'completed') return `${baseTitle} (✅ Upload completed)`;
  if (uploadStatus === 'failed') {
    const summary = summarizeUploadFailureMessages(failureDetails);
    return summary
      ? `${baseTitle} (❌ Upload failed — ${summary})`
      : `${baseTitle} (❌ Upload failed)`;
  }
  return baseTitle;
}

/**
 * Popup to show selected file overview (3–5 rows preview) with Apply and Cancel.
 * Apply/Cancel both close and reset; API integration can be added to onApply later.
 */
const UploadPreviewPopUp = ({
  open = false,
  onClose,
  title = 'Upload - File overview',
  fileName = '',
  previewHeaders = [],
  previewRows = [],
  onApply,
  onCancel,
  loading = false,
  invalidPreviewRows = [],
  disableApply = false,
  summary = null,
  uploadStatus = null,
  failureDetails = null,
}) => {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (onCancel) onCancel();
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose, onCancel]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) handleCancel();
  };

  const handleApply = async () => {
    if (!onApply) {
      onClose();
      return;
    }
  
    const result = await Promise.resolve(onApply());
  
    // ❗ IMPORTANT: only close if explicitly returned true
    if (result === true) {
      onClose();
    }
  };

  const handleCancel = () => {
    if (loading) return; // Prevent cancel during loading
    if (onCancel) onCancel();
    onClose();
  };

  const displayTitle = useMemo(
    () =>
      uploadStatus
        ? buildUploadPreviewTitle(title, uploadStatus, failureDetails)
        : title,
    [title, uploadStatus, failureDetails]
  );

  if (!open) return null;

  const hasPreview = previewHeaders.length > 0 && previewRows.length > 0;

  const popupContent = (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 99999 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="upload-preview-title"
    >
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />
      <div
        className="relative w-full max-w-2xl max-h-[90vh] mx-4 bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 pt-5 pb-2 border-b border-gray-100">
          <h2 id="upload-preview-title" className="text-lg font-semibold text-gray-900">
            {displayTitle}
          </h2>
          <button
            type="button"
            onClick={handleCancel}
            disabled={loading}
            className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-60 disabled:cursor-not-allowed"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="h-5 w-5"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 overflow-auto flex-1 min-h-0">
          {fileName && (
            <p className="text-sm text-gray-600 mb-3">
              <span className="font-medium">File:</span> {fileName}
            </p>
          )}
          {summary && (
            <div className="mb-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              Total rows: <span className="font-semibold">{summary.totalRows ?? 0}</span> | Valid rows:{" "}
              <span className="font-semibold text-emerald-700">{summary.validRows ?? 0}</span> | Invalid rows:{" "}
              <span className="font-semibold text-red-600">{summary.invalidRows ?? 0}</span>
              {/* {summary.totalGrossWeight != null && (
                <div className="mt-1">
                  Total Gross Weight:{" "}
                  <span className="font-semibold">
                    {Number(summary.totalGrossWeight || 0).toFixed(2)} kg
                  </span>
                </div>
              )} */}
            </div>
          )}
          {hasPreview ? (
            <div className="border border-gray-200 rounded-lg overflow-x-auto overflow-y-hidden">
              <table className="w-max min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    {previewHeaders.map((h, i) => (
                      <th
                        key={i}
                        className="px-3 py-2 text-left font-medium text-gray-700 whitespace-nowrap"
                      >
                        {h || `Column ${i + 1}`}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, rIdx) => (
                    <tr
                      key={rIdx}
                      className={`border-b border-gray-100 last:border-b-0 ${
                        invalidPreviewRows.includes(rIdx) ? "bg-red-50 hover:bg-red-50" : "hover:bg-gray-50/50"
                      }`}
                    >
                      {previewHeaders.map((key, cIdx) => (
                        <td key={cIdx} className="px-3 py-2 text-gray-800 whitespace-nowrap">
                          {row[key] ?? '—'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No rows to preview.</p>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 pb-6 pt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={handleCancel}
            disabled={loading}
            className="h-10 rounded-full border border-gray-300 bg-white px-5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={loading || disableApply}
            className="h-10 rounded-full border border-emerald-500 bg-emerald-500 px-5 text-sm font-medium text-white hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading && (
              <svg
                className="animate-spin h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {loading ? 'Uploading...' : 'Apply'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(popupContent, document.body);
};

export default UploadPreviewPopUp;
